/**
 * Pick an account the way the chart of accounts (شجرة الحسابات) is laid
 * out: the main account first, then the sub-account under it.
 *
 *   Main account · الحساب الرئيسي   any group in the tree - "1 الموجودات",
 *                                   "11 الموجودات المتداولة", "113 نقدي في
 *                                   البنك" - left empty to see every one
 *   Sub account · الحساب الفرعي     only what can be picked under that main
 *                                   account, at any depth; all of them when
 *                                   no main account is picked
 *
 * Picking a sub-account straight away (by typing its name) fills in its
 * main account, so the form always says where in the tree it sits.
 *
 * `options` are {id, code?, name, path: [{id, code, name}]} - path is the
 * accounts above it, root first, as the server sends it (Ledger::shape).
 * One without a path (a box the chart does not hold yet) is still offered,
 * just under no main account.
 */

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import SuiBox from "components/SuiBox";
import { Pick } from "./Fields";

const codeName = (a) => [a.code, a.name].filter(Boolean).join(" ");

/** Every group the options sit under, each once, in tree order. */
export function mainAccounts(options) {
  const byId = new Map();
  options.forEach((o) =>
    (o.path || []).forEach((g, depth) => {
      if (!byId.has(String(g.id))) {
        // the chain of codes down to it - sorting on it keeps children under their parent
        const chain = o.path.slice(0, depth + 1).map((p) => String(p.code || ""));
        byId.set(String(g.id), { ...g, depth, chain });
      }
    })
  );
  const compare = (a, b) => {
    for (let i = 0; i < Math.min(a.chain.length, b.chain.length); i += 1) {
      const c = a.chain[i].localeCompare(b.chain[i], "en", { numeric: true });
      if (c) return c;
    }
    return a.chain.length - b.chain.length;
  };
  return [...byId.values()].sort(compare);
}

/** Whether `option` sits anywhere under the main account `mainId`. */
const under = (option, mainId) =>
  !mainId || (option.path || []).some((g) => String(g.id) === String(mainId));

function AccountTreePick({
  label,
  mainLabel,
  options,
  value,
  onChange,
  getLabel,
  getHint,
  isDisabled,
  noOptionsText,
  helperText,
  stacked,
}) {
  const mains = useMemo(() => mainAccounts(options), [options]);
  const picked = options.find((o) => String(o.id) === String(value)) || null;
  const [main, setMain] = useState("");

  /*
   * A sub-account picked straight away (or a saved one loaded) shows where
   * it sits: its own parent becomes the main account, unless the main one
   * picked already holds it. Only when the sub-account changes - clearing
   * the main account afterwards to see them all is left alone. Its parent
   * counts too: a box is known before the chart says where it sits.
   */
  const parent = (picked?.path || []).slice(-1)[0];
  const placed = picked ? `${picked.id}|${parent?.id ?? ""}` : "";
  useEffect(() => {
    if (!parent || (main && under(picked, main))) return;
    setMain(parent.id);
  }, [placed]);

  const subs = options.filter((o) => under(o, main));

  // the parent goes in the hint when the main account does not already say it
  const hintOf = (o) => {
    if (getHint) return getHint(o);
    const above = (o.path || []).slice(-1)[0];
    return above && String(above.id) !== String(main) ? codeName(above) : "";
  };

  const pickMain = (id) => {
    setMain(id);
    // the sub-account picked before is not under the new main account
    if (picked && id && !under(picked, id)) onChange("");
  };

  return (
    <SuiBox
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: stacked ? "1fr" : "minmax(0, 1fr) minmax(0, 1.3fr)" },
        columnGap: 1.5,
        alignItems: "start",
      }}
    >
      <Pick
        label={mainLabel}
        options={mains}
        value={main}
        onChange={pickMain}
        getLabel={codeName}
        getDepth={(g) => g.depth}
        placeholder="All · الكل"
        noOptionsText="No main accounts"
      />
      <Pick
        label={label}
        options={subs}
        value={value}
        onChange={onChange}
        getLabel={getLabel || codeName}
        getHint={hintOf}
        isDisabled={isDisabled}
        noOptionsText={noOptionsText}
        helperText={helperText}
        placeholder={main ? "Choose..." : "All sub-accounts · كل الفرعية"}
      />
    </SuiBox>
  );
}

AccountTreePick.defaultProps = {
  mainLabel: "Main account · الحساب الرئيسي",
  value: "",
  getLabel: null,
  getHint: null,
  isDisabled: null,
  noOptionsText: "No accounts - add them on the Chart tab",
  helperText: " ",
  stacked: false,
};

AccountTreePick.propTypes = {
  label: PropTypes.node.isRequired,
  mainLabel: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      path: PropTypes.arrayOf(PropTypes.object),
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  getLabel: PropTypes.func,
  getHint: PropTypes.func,
  isDisabled: PropTypes.func,
  noOptionsText: PropTypes.node,
  helperText: PropTypes.node,
  stacked: PropTypes.bool,
};

export default AccountTreePick;

/* ------------------------------------------------------------------ */
/* the things the chart holds, placed in it                             */
/* ------------------------------------------------------------------ */

const BOX_LINKS = ["box_cash", "box_bank"];

/** The chart account of a box (payment_sources id) or a kind (acc_categories id). */
const linkOf = (links, types, id) =>
  (links || []).find((l) => types.includes(l.link_type) && String(l.link_id) === String(id));

/**
 * The boxes and banks, each placed where the chart holds it (under 111 or
 * 113) - `links` is GET /accounts/postable?for=links.
 */
export const boxOptions = (boxes, links) =>
  (boxes || []).map((b) => {
    const at = linkOf(links, BOX_LINKS, b.id);
    return { ...b, code: at?.code || "", path: at?.path || [] };
  });

/**
 * The expense or income kinds (`type`), placed where the chart holds them,
 * with ids "c:<category id>" so they sit in one list with plain accounts.
 */
export const kindOptions = (categories, links, type) =>
  (categories || [])
    .filter((c) => c.type === type)
    .map((c) => {
      const at = linkOf(links, [type], c.id);
      return {
        id: `c:${c.id}`,
        category_id: c.id,
        code: at?.code || "",
        name: c.name,
        is_active: c.is_active,
        path: at?.path || [],
      };
    });
